/**
 * `GET /event-types?category=…` — stub, standing in until the endpoint ships.
 *
 * The technical values are the ones LIVE DATA ACTUALLY SENDS (verified against
 * StarRocks `fincense.activity.activity_type` and
 * `transactions.transaction_type_code`), NOT the design's `login` / `credit_card`
 * examples. The design shows lowercase; the pipeline sends uppercase, and the
 * story requires an exact match — so a config written from the design would match
 * nothing. Raised 2026-08-24, unresolved.
 *
 * Field counts here are illustrative; the real ones come from the BE.
 */
import { EVENT_CATEGORY } from '../constants';

export const EVENT_TYPES = {
  [EVENT_CATEGORY.ACTIVITY]: [
    {
      id: 1,
      technicalValue: 'LOGIN',
      displayName: 'Login',
      iconKey: 'login',
      isDefault: true,
      spatialFieldCount: 3,
      onClickFieldCount: 5
    },
    {
      id: 2,
      technicalValue: 'LOGIN_MFA_WITH_OTP',
      displayName: 'Login MFA with OTP',
      iconKey: 'message',
      spatialFieldCount: 3,
      onClickFieldCount: 6
    },
    {
      id: 3,
      technicalValue: 'ACCOUNT_PASSWORD_RESET',
      displayName: 'Password Reset',
      iconKey: 'key',
      spatialFieldCount: 2,
      onClickFieldCount: 5
    },
    {
      id: 4,
      technicalValue: 'PASSWORD_CHANGED',
      displayName: 'Password Changed',
      iconKey: 'key',
      spatialFieldCount: 2,
      onClickFieldCount: 5
    },
    {
      id: 5,
      technicalValue: 'MODIFY_TRANSACTION_SETTINGS',
      displayName: 'Transaction Settings Changed',
      iconKey: 'warning',
      isNew: true,
      spatialFieldCount: 3,
      onClickFieldCount: 4
    }
  ],

  [EVENT_CATEGORY.TRANSACTION]: [
    {
      id: 6,
      technicalValue: 'XBORDER',
      displayName: 'Cross Border Remittance',
      iconKey: 'landmark',
      isDefault: true,
      spatialFieldCount: 3,
      onClickFieldCount: 8
    }
  ]
};

const f = (fieldKey, displayLabel, showSpatial, showOnClick, extra = {}) => ({
  fieldKey,
  displayLabel,
  showSpatial,
  showOnClick,
  sortOrder: 0,
  ...extra
});

/**
 * `GET /event-types/:id/fields` — the configured fields for one type.
 *
 * EVERY type has an entry. Previously only Login did, so selecting anything else
 * showed an empty table while the list beside it claimed a count — which reads as
 * a broken screen rather than as missing mock data.
 *
 * `isDeprecated` on QA's `old_channel` is the PRD's own example of a field that has
 * left the schema: the BE computes this flag at read time, and the row must stay
 * visible rather than being silently dropped.
 */
export const EVENT_TYPE_FIELDS = {
  1: [
    {
      fieldKey: 'activity_id',
      displayLabel: 'Activity ID',
      showSpatial: true,
      showOnClick: true,
      sortOrder: 0
    },
    {
      fieldKey: 'device_fingerprint',
      displayLabel: 'Device',
      showSpatial: true,
      showOnClick: true,
      sortOrder: 1
    },
    {
      fieldKey: 'ip_address',
      displayLabel: 'IP Address',
      showSpatial: true,
      showOnClick: true,
      sortOrder: 2
    },
    {
      fieldKey: 'activity_status',
      displayLabel: 'Status',
      showSpatial: false,
      showOnClick: true,
      sortOrder: 3
    },
    {
      fieldKey: 'user_id',
      displayLabel: 'User ID',
      showSpatial: false,
      showOnClick: true,
      sortOrder: 4
    }
  ]
};

// 2 — Login MFA with OTP: carries a field that has left the schema.
EVENT_TYPE_FIELDS[2] = [
  f('activity_id', 'Activity ID', true, true),
  f('device_fingerprint', 'Device', true, true, { sortOrder: 1 }),
  f('ip_address', 'IP Address', true, true, { sortOrder: 2 }),
  f('activity_status', 'Status', false, true, { sortOrder: 3 }),
  f('old_channel', 'OTP Channel', false, true, {
    sortOrder: 4,
    isDeprecated: true
  })
];

// 3 — Password Reset
EVENT_TYPE_FIELDS[3] = [
  f('activity_id', 'Activity ID', true, true),
  f('ip_address', 'IP Address', true, true, { sortOrder: 1 }),
  f('activity_status', 'Status', false, true, { sortOrder: 2 })
];

// 4 — Password Changed
EVENT_TYPE_FIELDS[4] = [
  f('activity_id', 'Activity ID', true, true),
  f('device_fingerprint', 'Device', true, true, { sortOrder: 1 }),
  f('activity_status', 'Status', false, true, { sortOrder: 2 })
];

// 5 — Transaction Settings Changed
EVENT_TYPE_FIELDS[5] = [
  f('activity_id', 'Activity ID', true, true),
  f('device_fingerprint', 'Device', true, true, { sortOrder: 1 }),
  f('ip_address', 'IP Address', true, true, { sortOrder: 2 }),
  f('user_id', 'User ID', false, true, { sortOrder: 3 })
];

// 6 — Cross Border Remittance (TRANSACTION). Its identifier is transaction_id.
EVENT_TYPE_FIELDS[6] = [
  f('transaction_id', 'Transaction ID', true, true),
  f('transaction_amount', 'Amount', true, true, { sortOrder: 1 }),
  f('beneficiary', 'Beneficiary', true, true, { sortOrder: 2 }),
  f('transaction_currency', 'Currency', false, true, { sortOrder: 3 }),
  f('merchant_name', 'Merchant', false, true, { sortOrder: 4 })
];

/**
 * COUNTS ARE DERIVED, not typed. They were hand-written beside a field list that
 * did not exist, so the list said "5 on click" while the table showed four rows.
 * The real endpoint counts the rows too — this keeps the mock honest the same way.
 */
Object.values(EVENT_TYPES).forEach((list) =>
  list.forEach((type) => {
    const fields = EVENT_TYPE_FIELDS[type.id] || [];
    /* eslint-disable no-param-reassign */
    type.spatialFieldCount = fields.filter((x) => x.showSpatial).length;
    type.onClickFieldCount = fields.filter((x) => x.showOnClick).length;
    /* eslint-enable no-param-reassign */
  })
);

export default EVENT_TYPES;
